import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AppInitService } from 'src/app/app-init.service';
import { AppConfigurations } from 'src/app/shared/models/appConfiguration.model';
import { ToastService } from 'src/app/shared/services/toast.service';
import { TenantModel } from '../models/tenant';
import { TenantBasicDetail } from '../models/tenant-basic-detail';
import { TenantConfigurations } from '../models/tenant-configurations';
import { AuditLog } from '../models/auditLog';

@Injectable({
    providedIn: 'root'
  })
export class TenantService {
  
  private tenantServiceTenantController?: string;
  private identityServerTenantController?: string;
  
  private appConfig?: AppConfigurations;
  
  constructor(private appInitService: AppInitService, private http: HttpClient, private toastService: ToastService) {
    appInitService.getAppConfiguration$().subscribe({
      next: (config) => this._onReceiveAppConfig(config)
    })
  }
  
  private _onReceiveAppConfig(appConfig : AppConfigurations){
    this.appConfig = appConfig;
    this.identityServerTenantController =  `${this.appConfig.identityServerApi.baseApiUrl}${this.appConfig.identityServerApi.tenantController}`;
    this.tenantServiceTenantController = `${this.appConfig.tenantApi.baseApiUrl}${this.appConfig.tenantApi.tenantController}`
  }
  /*
  * Fetch all the tenants user can be switched into
  */
  public fetchAvailableTenants(): Observable<TenantModel[]> {
    return this.http.get<TenantModel[]>(`${this.identityServerTenantController}/available`);
  }

  public getTenantConfiguration(): Observable<TenantConfigurations> {
    return this.http.get<TenantConfigurations>(`${this.identityServerTenantController}/configuration`)
    .pipe(
      catchError(this.handleError<TenantConfigurations>('getTenantConfiguration', undefined))
    );
  }

  public getTenantBasicDetail(tenantId: number): Observable<TenantBasicDetail> {
    return this.http.get<TenantBasicDetail>(`${this.tenantServiceTenantController}/${tenantId}/tenantdetails`)
    .pipe(
      catchError(this.handleError<TenantBasicDetail>('getTenantBasicDetail', undefined))
    );
  }

  public updateTenantBasicDetail(tenantDetails: TenantBasicDetail): Observable<string> {
    return this.http.put<string>(`${this.tenantServiceTenantController}`, tenantDetails)
    .pipe(
      catchError(this.handleError<string>('updateTenantBasicDetail', ''))
    );
  }

  public uploadClientLogo(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<string>(`${this.tenantServiceTenantController}/clientlogo`, formData)
    .pipe(
      catchError((error) => {
        this.toastService.showError('_Tenant_Client_Logo_Upload_Fail_Text_');
        return throwError(() => error);
      })
    );
  }

  public deleteClientLogo(): Observable<string> {
    return this.http.delete<string>(`${this.tenantServiceTenantController}/clientlogo`)
    .pipe(
      catchError(this.handleError<string>('removeClientLogo', '', 'Client Logo remove failed!'))
    );
  }

  public uploadDeviceLogo(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<string>(`${this.tenantServiceTenantController}/devicelogo`, formData)
    .pipe(
      tap(() => this.toastService.showSuccess('_Tenant_Client_Device_Logo_Upload_Success_Text_')),
      catchError(this.handleError<string>('uploadDeviceLogo', '', '_Tenant_Client_Device_Logo_Upload_Fail_Text_'))
    );
  }

  public getLogoUrl(forDevice: boolean): Observable<any>{
    return this.http.get<any>(`${this.tenantServiceTenantController}/logo?forDevice=${forDevice}`)
    .pipe(
      catchError(this.handleError<any>('getLogoUrl', null))
    );
  }

  private handleError<T>(operation = 'operation', result?: T, message?: string) {
    return (error: any): Observable<T> => {
      console.error(error);
      if (message) {
        this.toastService.showError(message);
      }
      return of(result as T);
    };
  }

  public logTenantSwitch(auditLog: AuditLog): Observable<string> {
    return this.http.post<string>(`${this.identityServerTenantController}/logtenantswitch`, auditLog)
      .pipe(
        catchError(this.handleError<string>('logTenantSwitch', '', '_Tenant_Swtich_Audit_Fail_Text_'))
      );
  }
}
